import { CommonModule, NgTemplateOutlet } from '@angular/common'
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  forwardRef,
  NgModule,
  numberAttribute,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  input,
  model,
  inject,
  output,
} from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { PrimeTemplate } from '../api'

import { Nullable } from '../ts-helpers'
import { ObjectUtils } from '../utils'

import { SelectButtonChangeEvent, SelectButtonOptionClickEvent } from './selectbutton.interface'
import { Ripple } from '../ripple/ripple'
import { AutoFocus } from '../autofocus/autofocus'

export const SELECTBUTTON_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SelectButton),
  multi: true,
}
/**
 * SelectButton is used to choose single or multiple items from a list using buttons.
 * @group Components
 */
@Component({
  selector: 'p-selectButton',
  template: `
    <div
      #container
      [style]="style()"
      [class]="[styleClass(), 'p-selectbutton p-buttonset p-component'].join(' ')"
      role="group"
      [attr.aria-labelledby]="ariaLabelledBy()"
      [attr.data-pc-name]="'selectbutton'"
      [attr.data-pc-section]="'root'"
    >
      @for (option of options(); track $index; let i = $index) {
        <div
          pRipple
          [attr.tabindex]="i === focusedIndex && !disabled() ? '0' : '-1'"
          [attr.aria-label]="option.label"
          [role]="multiple() ? 'checkbox' : 'radio'"
          [attr.aria-checked]="isSelected(option)"
          [attr.aria-disabled]="optionDisabled()"
          [class]="'p-button p-component' + (option.styleClass ? ' ' + option.styleClass : '')"
          [class.p-highlight]="isSelected(option)"
          [class.p-disabled]="disabled() || isOptionDisabled(option)"
          [class.p-button-icon-only]="option.icon && !getOptionLabel(option)"
          (click)="onOptionSelect($event, option, i)"
          (keydown)="onKeyDown($event, option, i)"
          [attr.title]="option.title"
          (focus)="onFocus($event, i)"
          (blur)="onBlur()"
          [attr.aria-labelledby]="this.getOptionLabel(option)"
          [attr.data-pc-section]="'button'"
          pAutoFocus
        >
          @if (!itemTemplate) {
            @if (option.icon) {
              <span
                [class]="[option.icon, 'p-button-icon p-button-icon-left'].join(' ')"
                [attr.data-pc-section]="'icon'"
              ></span>
            }
            <span class="p-button-label" [attr.data-pc-section]="'label'">{{
              getOptionLabel(option)
            }}</span>
          } @else {
            <ng-container
              *ngTemplateOutlet="selectButtonTemplate; context: { $implicit: option, index: i }"
            />
          }
        </div>
      }
    </div>
  `,
  providers: [SELECTBUTTON_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['../button/button.css'],
  host: {
    class: 'p-element',
  },
  imports: [Ripple, AutoFocus, NgTemplateOutlet],
})
export class SelectButton implements ControlValueAccessor {
  cd = inject(ChangeDetectorRef)

  /**
   * An array of selectitems to display as the available options.
   * @group Props
   */
  readonly options = input<
    any[] | undefined
    /**
     * Name of the label field of an option.
     * @group Props
     */
  >(undefined)
  /**
   * Name of the label field of an option.
   * @group Props
   */
  readonly optionLabel = input<
    string | undefined
    /**
     * Name of the value field of an option.
     * @group Props
     */
  >(undefined)
  /**
   * Name of the value field of an option.
   * @group Props
   */
  readonly optionValue = input<
    string | undefined
    /**
     * Name of the disabled field of an option.
     * @group Props
     */
  >(undefined)
  /**
   * Name of the disabled field of an option.
   * @group Props
   */
  readonly optionDisabled = input<
    string | undefined
    /**
     * Whether selection can be cleared.
     * @group Props
     */
  >(undefined)
  /**
   * Whether selection can be cleared.
   * @group Props
   */
  readonly unselectable = input<boolean, unknown>(
    false,
    /**
     * Index of the element in tabbing order.
     * @group Props
     */
    { transform: booleanAttribute },
  )
  /**
   * Index of the element in tabbing order.
   * @group Props
   */
  readonly tabindex = input<number, unknown>(
    0,
    /**
     * When specified, allows selecting multiple values.
     * @group Props
     */
    { transform: numberAttribute },
  )
  /**
   * When specified, allows selecting multiple values.
   * @group Props
   */
  readonly multiple = input<
    boolean | undefined,
    /**
     * Whether selection can not be cleared.
     * @group Props
     */
    unknown
  >(undefined, { transform: booleanAttribute })
  /**
   * Whether selection can not be cleared.
   * @group Props
   */
  readonly allowEmpty = input<boolean, unknown>(
    true,
    /**
     * Inline style of the component.
     * @group Props
     */
    { transform: booleanAttribute },
  )
  /**
   * Inline style of the component.
   * @group Props
   */
  readonly style = input<
    any
    /**
     * Style class of the component.
     * @group Props
     */
  >(undefined)
  /**
   * Style class of the component.
   * @group Props
   */
  readonly styleClass = input<
    string | undefined
    /**
     * Establishes relationships between the component and label(s) where its value should be one or more element IDs.
     * @group Props
     */
  >(undefined)
  /**
   * Establishes relationships between the component and label(s) where its value should be one or more element IDs.
   * @group Props
   */
  readonly ariaLabelledBy = input<
    string | undefined
    /**
     * When present, it specifies that the element should be disabled.
     * @group Props
     */
  >(undefined)
  /**
   * When present, it specifies that the element should be disabled.
   * @group Props
   */
  readonly disabled = model<boolean | undefined>(undefined)
  /**
   * A property to uniquely identify a value in options.
   * @group Props
   */
  readonly dataKey = input<
    string | undefined
    /**
     * When present, it specifies that the component should automatically get focus on load.
     * @group Props
     */
  >(undefined)
  /**
   * When present, it specifies that the component should automatically get focus on load.
   * @group Props
   */
  readonly autofocus = input<
    boolean | undefined,
    /**
     * Callback to invoke on input click.
     * @param {SelectButtonOptionClickEvent} event - Custom click event.
     * @group Emits
     */
    unknown
  >(undefined, { transform: booleanAttribute })
  /**
   * Callback to invoke on input click.
   * @param {SelectButtonOptionClickEvent} event - Custom click event.
   * @group Emits
   */
  readonly onOptionClick = output<SelectButtonOptionClickEvent>()
  /**
   * Callback to invoke on selection change.
   * @param {SelectButtonChangeEvent} event - Custom change event.
   * @group Emits
   */
  readonly onChange = output<SelectButtonChangeEvent>()

  @ViewChild('container') container: Nullable<ElementRef>

  @ContentChild(PrimeTemplate) itemTemplate!: PrimeTemplate

  public get selectButtonTemplate(): TemplateRef<any> {
    return this.itemTemplate?.template
  }

  get equalityKey() {
    return this.optionValue() ? null : this.dataKey()
  }

  value: any

  onModelChange: (...args: unknown[]) => unknown = () => {}

  onModelTouched: (...args: unknown[]) => unknown = () => {}

  focusedIndex = 0

  getOptionLabel(option: any) {
    const optionLabel = this.optionLabel()
    return optionLabel
      ? ObjectUtils.resolveFieldData(option, optionLabel)
      : option.label != undefined
        ? option.label
        : option
  }

  getOptionValue(option: any) {
    const optionValue = this.optionValue()
    return optionValue
      ? ObjectUtils.resolveFieldData(option, optionValue)
      : this.optionLabel() || option.value === undefined
        ? option
        : option.value
  }

  isOptionDisabled(option: any) {
    const optionDisabled = this.optionDisabled()
    return optionDisabled
      ? ObjectUtils.resolveFieldData(option, optionDisabled)
      : option.disabled !== undefined
        ? option.disabled
        : false
  }

  writeValue(value: any): void {
    this.value = value
    this.cd.markForCheck()
  }

  registerOnChange(fn: (...args: unknown[]) => unknown): void {
    this.onModelChange = fn
  }

  registerOnTouched(fn: (...args: unknown[]) => unknown): void {
    this.onModelTouched = fn
  }

  setDisabledState(val: boolean): void {
    this.disabled.set(val)
    this.cd.markForCheck()
  }

  onOptionSelect(event, option, index) {
    if (this.disabled() || this.isOptionDisabled(option)) {
      return
    }

    const selected = this.isSelected(option)

    if (selected && this.unselectable()) {
      return
    }

    const optionValue = this.getOptionValue(option)
    let newValue

    if (this.multiple()) {
      if (selected) {
        newValue = this.value.filter(
          (val) => !ObjectUtils.equals(val, optionValue, this.equalityKey),
        )
      } else {
        newValue = this.value ? [...this.value, optionValue] : [optionValue]
      }
    } else {
      if (selected && !this.allowEmpty()) {
        return
      }
      newValue = selected ? null : optionValue
    }

    this.focusedIndex = index
    this.value = newValue
    this.onModelChange(this.value)

    this.onChange.emit({
      originalEvent: event,
      value: this.value,
    })

    this.onOptionClick.emit({
      originalEvent: event,
      option: option,
      index: index,
    })
  }

  onKeyDown(event, option, index) {
    switch (event.code) {
      case 'Space': {
        this.onOptionSelect(event, option, index)
        event.preventDefault()
        break
      }

      case 'ArrowDown':

      case 'ArrowRight': {
        this.changeTabIndexes(event, 'next')
        event.preventDefault()
        break
      }

      case 'ArrowUp':

      case 'ArrowLeft': {
        this.changeTabIndexes(event, 'prev')
        event.preventDefault()
        break
      }

      default:
        //no op
        break
    }
  }

  changeTabIndexes(event, direction) {
    let firstTabableChild, index

    for (let i = 0; i <= this.container.nativeElement.children.length - 1; i++) {
      if (this.container.nativeElement.children[i].getAttribute('tabindex') === '0') {
        firstTabableChild = { elem: this.container.nativeElement.children[i], index: i }
      }
    }

    if (direction === 'prev') {
      if (firstTabableChild.index === 0) {
        index = this.container.nativeElement.children.length - 1
      } else {
        index = firstTabableChild.index - 1
      }
    } else {
      if (firstTabableChild.index === this.container.nativeElement.children.length - 1) {
        index = 0
      } else {
        index = firstTabableChild.index + 1
      }
    }

    this.focusedIndex = index
    this.container.nativeElement.children[index].focus()
  }

  onFocus(event: Event, index: number) {
    this.focusedIndex = index
  }

  onBlur() {
    this.onModelTouched()
  }

  removeOption(option: any): void {
    this.value = this.value.filter(
      (val: any) => !ObjectUtils.equals(val, this.getOptionValue(option), this.dataKey()),
    )
  }

  isSelected(option: any) {
    let selected = false
    const optionValue = this.getOptionValue(option)

    if (this.multiple()) {
      if (this.value && Array.isArray(this.value)) {
        for (const val of this.value) {
          if (ObjectUtils.equals(val, optionValue, this.dataKey())) {
            selected = true
            break
          }
        }
      }
    } else {
      selected = ObjectUtils.equals(this.getOptionValue(option), this.value, this.equalityKey)
    }

    return selected
  }
}
